import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import {
  ArrowRight,
  User,
  Building2,
  Mail,
  Lock,
  Eye,
  EyeOff,
  ShieldCheck,
  DollarSign,
  Link2,
  Brain,
  Sparkles,
} from "lucide-react";

// VIS-002 · Public signup page. Bilingual (EN + ES). Approved production
// composition (v3 background). Native UI copy sits over the intentional
// open left field so it never overlaps Levi. Right panel is native UI.
// Existing signup functionality — AuthContext.register, /app/setup
// redirect, form validation, Sign-in link, Privacy Policy link — is
// preserved without modification.

const COPY_SIGNUP = {
  en: {
    // Left panel
    tagline: "Built by a contractor, for contractors.",
    kicker: "You\u2019re almost there.",
    h1_a: "Meet the coworker",
    h1_b_pre: "who ",
    h1_b_hilite: "never forgets",
    h1_c: "the details.",
    lead: "Levi is ready to work the way you work.",
    sub: "Set up your business, give Levi your pricing, and start running your jobs from one place.",
    body:
      "Talk to him. Show him the job. Ask him what happened on a project three months ago. Levi keeps your customers, jobs, estimates, documents, schedules and communication connected\u2014so you don\u2019t have to keep it all in your head.",
    cta1_title: "Your pricing stays yours.",
    cta1_body: "Levi works from the numbers and rules you approve.",
    cta2_title: "Your jobs stay connected.",
    cta2_body: "From the first customer conversation through the final payment.",
    cta3_title: "And Levi remembers.",
    cta3_body: "Customers. Projects. Decisions. Past jobs. The details you shouldn\u2019t have to.",
    pill_a: "Talk to Levi hands-free from the jobsite",
    pill_b: "or enter things yourself anytime.",

    // Right panel
    overline: "Let\u2019s get your business set up",
    panel_heading_a: "Create your",
    panel_heading_b: "JobClose AI account",
    panel_sub:
      "Create your account and Levi will guide you through the rest\u2014your company, trades, pricing and preferences, one simple step at a time.",
    already_prefix: "Already have an account?",
    already_link: "Sign in",
    ph_full_name: "Your full name",
    ph_company: "Company name",
    ph_email: "Work email",
    ph_password: "Choose a password (min 8 chars)",
    show_pw: "Show password",
    hide_pw: "Hide password",
    submit_idle: "Create my JobClose AI account",
    submit_loading: "Creating your account\u2026",
    // Pricing card
    price_trial: "Start your 7-day free trial.",
    price_main: "$129/month includes 2 full users.",
    price_line1: "Everything in JobClose AI. No feature gating.",
    price_line2: "Additional users $20/month.",
    price_onboard: "Standard onboarding included.",
    // Security row
    security: "Your information is secure and never shared.",
    privacy: "Privacy Policy",
    // Toast fallbacks
    toast_success: "Create your JobClose AI account",
    toast_error: "Something went wrong. Please try again.",
  },
  es: {
    // Left panel
    tagline: "Creado por un contratista, para contratistas.",
    kicker: "Ya casi terminas.",
    h1_a: "Conoce al compa\u00f1ero",
    h1_b_pre: "que ",
    h1_b_hilite: "nunca olvida",
    h1_c: "los detalles.",
    lead: "Levi est\u00e1 listo para trabajar como t\u00fa trabajas.",
    sub:
      "Configura tu negocio, dale a Levi tus precios y empieza a manejar tus trabajos desde un solo lugar.",
    body:
      "Habla con \u00e9l. Mu\u00e9strale el trabajo. Preg\u00fantale qu\u00e9 pas\u00f3 en un proyecto hace tres meses. Levi mantiene conectados a tus clientes, trabajos, estimaciones, documentos, calendarios y comunicaci\u00f3n \u2014 para que no tengas que retenerlo todo en la cabeza.",
    cta1_title: "Tus precios siguen siendo tuyos.",
    cta1_body: "Levi trabaja con los n\u00fameros y las reglas que t\u00fa apruebas.",
    cta2_title: "Tus trabajos siguen conectados.",
    cta2_body: "Desde la primera conversaci\u00f3n con el cliente hasta el pago final.",
    cta3_title: "Y Levi recuerda.",
    cta3_body:
      "Clientes. Proyectos. Decisiones. Trabajos anteriores. Los detalles que no deber\u00edas tener que recordar.",
    pill_a: "Habla con Levi manos libres desde la obra",
    pill_b: "o ingresa la informaci\u00f3n t\u00fa mismo en cualquier momento.",

    // Right panel
    overline: "Vamos a configurar tu negocio",
    panel_heading_a: "Crea tu cuenta",
    panel_heading_b: "de JobClose AI",
    panel_sub:
      "Crea tu cuenta y Levi te guiar\u00e1 con el resto \u2014 tu empresa, oficios, precios y preferencias, un paso simple a la vez.",
    already_prefix: "\u00bfYa tienes una cuenta?",
    already_link: "Iniciar sesi\u00f3n",
    ph_full_name: "Tu nombre completo",
    ph_company: "Nombre de la empresa",
    ph_email: "Correo del trabajo",
    ph_password: "Elige una contrase\u00f1a (m\u00edn. 8)",
    show_pw: "Mostrar contrase\u00f1a",
    hide_pw: "Ocultar contrase\u00f1a",
    submit_idle: "Crear mi cuenta de JobClose AI",
    submit_loading: "Creando tu cuenta\u2026",
    // Pricing card
    price_trial: "Empieza tu prueba gratis de 7 d\u00edas.",
    price_main: "$129/mes incluye 2 usuarios completos.",
    price_line1: "Todo lo que ofrece JobClose AI. Sin funciones bloqueadas.",
    price_line2: "Usuarios adicionales $20/mes.",
    price_onboard: "Incluye onboarding est\u00e1ndar.",
    // Security row
    security: "Tu informaci\u00f3n es segura y nunca se comparte.",
    privacy: "Pol\u00edtica de privacidad",
    // Toast fallbacks
    toast_success: "Crea tu cuenta de JobClose AI",
    toast_error: "Algo sali\u00f3 mal. Por favor, int\u00e9ntalo de nuevo.",
  },
};

const Signup = () => {
  const { lang, setLang } = useLang();
  const location = useLocation();

  // QA escape hatch: allow `/signup?lang=es` (or `?lang=en`) as a direct,
  // clickable Spanish/English deep-link for Product Owner visual review
  // without opening DevTools. Runs once per mount when the URL param
  // differs from the current locale; harmless when absent.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = (params.get("lang") || "").toLowerCase();
    if ((q === "es" || q === "en") && q !== lang) {
      setLang(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const c = COPY_SIGNUP[lang] || COPY_SIGNUP.en;
  const [form, setForm] = useState({
    full_name: "",
    company_name: "",
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({ ...form, role: "contractor" });
      toast.success(c.toast_success);
      navigate("/app/setup");
    } catch (err) {
      toast.error(err?.response?.data?.detail || c.toast_error);
    } finally {
      setLoading(false);
    }
  };

  const inputBase =
    "w-full pl-11 pr-3.5 py-3 rounded-lg border border-[var(--jc-border)] bg-white text-[var(--jc-text)] placeholder:text-[var(--jc-text-soft)] focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)] focus:border-[var(--jc-blue)] transition";

  return (
    <div className="min-h-screen grid lg:grid-cols-[8fr_5fr] bg-white" data-jc-lang={lang}>
      {/* LEFT PANEL — v3 approved production workshop background. */}
      <div
        className="hidden lg:flex relative flex-col overflow-hidden bg-white"
        style={{
          backgroundImage: "url('/brand/signup-left-panel-levi-workshop-v3.png')",
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
        }}
        data-testid="signup-left-panel"
      >
        <div className="relative p-10 xl:p-14 flex flex-col h-full">
          {/* Brand + tagline */}
          <Link
            to="/"
            data-testid="signup-logo"
            aria-label="JobClose AI"
            className="inline-flex items-center gap-3 select-none"
          >
            <img
              src="/brand/jobclose-ai-approved-logo-mark.png"
              alt=""
              aria-hidden="true"
              style={{ height: 56, width: "auto" }}
              className="shrink-0"
              draggable={false}
            />
            <span className="font-display text-3xl xl:text-4xl font-bold tracking-tight text-[var(--jc-text)] whitespace-nowrap leading-none">
              JobClose <span className="text-[var(--jc-blue)]">AI</span>
            </span>
          </Link>
          <div className="mt-2 text-[11.5px] font-bold tracking-[0.18em] uppercase text-[var(--jc-text-soft)]">
            {c.tagline}
          </div>

          {/* Kicker + headline block. Constrained width keeps the copy
              entirely inside the open left field of the background so
              Levi remains untouched. */}
          <div className="mt-10 xl:mt-14 max-w-[440px]">
            <div
              data-testid="signup-hero-kicker"
              className="text-[11.5px] font-bold tracking-[0.18em] uppercase text-[var(--jc-blue)]"
            >
              {c.kicker}
            </div>

            <h2
              data-testid="signup-hero-headline"
              className="mt-4 font-display font-extrabold text-4xl xl:text-5xl leading-[1.02] tracking-tight text-[var(--jc-text)]"
            >
              {c.h1_a}<br />
              {c.h1_b_pre}<span className="text-[var(--jc-blue)]">{c.h1_b_hilite}</span><br />
              {c.h1_c}
            </h2>

            <div className="mt-6 h-[3px] w-16 bg-[var(--jc-blue)] rounded-full" />

            <p className="mt-6 text-[17px] leading-[1.6] text-[var(--jc-text)] font-bold">
              {c.lead}
            </p>
            <p className="mt-2 text-[16px] leading-[1.6] text-[var(--jc-text)] font-medium">
              {c.sub}
            </p>

            <p className="mt-5 text-[16px] leading-[1.6] text-[var(--jc-text)] font-medium">
              {c.body}
            </p>

            {/* Three-row capability callouts — compacted vertical rhythm so
                the group sits higher and stays clear of the coffee mug and
                plans in the lower half of the composition. */}
            <div className="mt-6 space-y-3">
              <div className="flex items-start gap-3" data-testid="signup-callout-pricing">
                <span className="mt-0.5 flex-shrink-0 w-9 h-9 rounded-full bg-[var(--jc-blue)]/10 text-[var(--jc-blue)] grid place-items-center">
                  <DollarSign size={17} strokeWidth={2.6} />
                </span>
                <div className="min-w-0">
                  <div className="font-display font-extrabold text-[16.5px] leading-tight text-[var(--jc-text)]">
                    {c.cta1_title}
                  </div>
                  <div className="mt-0.5 max-w-[270px] text-[15px] leading-[1.45] text-[var(--jc-text)] font-medium">
                    {c.cta1_body}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3" data-testid="signup-callout-connected">
                <span className="mt-0.5 flex-shrink-0 w-9 h-9 rounded-full bg-[var(--jc-blue)]/10 text-[var(--jc-blue)] grid place-items-center">
                  <Link2 size={17} strokeWidth={2.6} />
                </span>
                <div className="min-w-0">
                  <div className="font-display font-extrabold text-[16.5px] leading-tight text-[var(--jc-text)]">
                    {c.cta2_title}
                  </div>
                  <div className="mt-0.5 max-w-[270px] text-[15px] leading-[1.45] text-[var(--jc-text)] font-medium">
                    {c.cta2_body}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3" data-testid="signup-callout-remembers">
                <span className="mt-0.5 flex-shrink-0 w-9 h-9 rounded-full bg-[var(--jc-blue)]/10 text-[var(--jc-blue)] grid place-items-center">
                  <Brain size={17} strokeWidth={2.6} />
                </span>
                <div className="min-w-0">
                  <div className="font-display font-extrabold text-[16.5px] leading-tight text-[var(--jc-text)]">
                    {c.cta3_title}
                  </div>
                  <div className="mt-0.5 max-w-[270px] text-[15px] leading-[1.45] text-[var(--jc-text)] font-medium">
                    {c.cta3_body}
                  </div>
                </div>
              </div>
            </div>

            {/* Talk to Levi hands-free footer callout */}
            <div
              data-testid="signup-hands-free-pill"
              className="mt-6 inline-flex items-start gap-3 rounded-xl bg-white/90 backdrop-blur-sm ring-1 ring-black/10 shadow-[0_10px_28px_-14px_rgba(15,23,42,0.25)] px-4 py-3"
            >
              <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-[var(--jc-blue)] text-white grid place-items-center">
                <Sparkles size={16} strokeWidth={2.6} />
              </span>
              <div className="text-[14.5px] leading-[1.5] text-[var(--jc-text)] font-bold">
                {c.pill_a}<br />
                <span className="font-medium text-[var(--jc-text)]">{c.pill_b}</span>
              </div>
            </div>
          </div>

          <div className="mt-auto" aria-hidden="true" />
        </div>
      </div>

      {/* RIGHT PANEL — approved signup form */}
      <div className="flex items-center justify-center p-6 sm:p-10 lg:p-14 bg-white">
        <div className="w-full max-w-md">
          {/* MOBILE-ONLY marketing stack — renders the same approved
              signup story before the form. Desktop keeps the left visual
              panel; on mobile we render it inline in this order:
              brand → kicker → headline → copy → Levi visual → 3 callouts
              → hands-free pill, then the existing overline/form/pricing
              /security continues below as items 8-11. */}
          <div className="lg:hidden mb-10 space-y-6" data-testid="signup-mobile-stack">
            {/* 1 · Compact brand header */}
            <div className="flex justify-center">
              <Logo
                variant="full"
                theme="dark"
                size="xl"
                to="/"
                data-testid="signup-logo-mobile"
              />
            </div>

            {/* 2 · Kicker + 3 · Headline + 4 · Supporting copy */}
            <div>
              <div
                data-testid="signup-hero-kicker-mobile"
                className="text-[11.5px] font-bold tracking-[0.18em] uppercase text-[var(--jc-blue)]"
              >
                {c.kicker}
              </div>
              <h2
                data-testid="signup-hero-headline-mobile"
                className="mt-3 font-display font-extrabold text-[34px] sm:text-[40px] leading-[1.05] tracking-tight text-[var(--jc-text)]"
              >
                {c.h1_a}<br />
                {c.h1_b_pre}<span className="text-[var(--jc-blue)]">{c.h1_b_hilite}</span><br />
                {c.h1_c}
              </h2>
              <div className="mt-5 h-[3px] w-16 bg-[var(--jc-blue)] rounded-full" />
              <p className="mt-5 text-[17px] leading-[1.6] text-[var(--jc-text)] font-bold">
                {c.lead}
              </p>
              <p className="mt-2 text-[16px] leading-[1.6] text-[var(--jc-text)] font-medium">
                {c.sub}
              </p>
              <p className="mt-4 text-[16px] leading-[1.6] text-[var(--jc-text)] font-medium">
                {c.body}
              </p>
            </div>

            {/* 5 · Levi visual — the same approved v3 workshop composition,
                rendered inline at natural landscape aspect so contractor
                (via the shelf/workspace scene) and Levi both remain
                comfortably visible without desktop cropping. */}
            <div
              className="relative -mx-6 sm:-mx-10 overflow-hidden ring-1 ring-black/10 shadow-[0_18px_40px_-16px_rgba(15,23,42,0.35)]"
              data-testid="signup-mobile-photo"
            >
              <img
                src="/brand/signup-left-panel-levi-workshop-v3.png"
                alt="Levi, a black Goldendoodle wearing a JobClose AI cap and a blue collar with a bone-shaped LEVI tag, sits at a workshop desk with today's plan notepad, tape measure, rolled plans, framed home photo, and a shelf reading Prepare · Communicate · Execute · Follow Up."
                className="block w-full h-auto select-none"
                draggable={false}
              />
            </div>

            {/* 6 · Three compact benefits */}
            <div className="space-y-3">
              <div className="flex items-start gap-3" data-testid="signup-callout-pricing-mobile">
                <span className="mt-0.5 flex-shrink-0 w-9 h-9 rounded-full bg-[var(--jc-blue)]/10 text-[var(--jc-blue)] grid place-items-center">
                  <DollarSign size={17} strokeWidth={2.6} />
                </span>
                <div className="min-w-0">
                  <div className="font-display font-extrabold text-[16.5px] leading-tight text-[var(--jc-text)]">
                    {c.cta1_title}
                  </div>
                  <div className="mt-0.5 text-[15px] leading-[1.45] text-[var(--jc-text)] font-medium">
                    {c.cta1_body}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3" data-testid="signup-callout-connected-mobile">
                <span className="mt-0.5 flex-shrink-0 w-9 h-9 rounded-full bg-[var(--jc-blue)]/10 text-[var(--jc-blue)] grid place-items-center">
                  <Link2 size={17} strokeWidth={2.6} />
                </span>
                <div className="min-w-0">
                  <div className="font-display font-extrabold text-[16.5px] leading-tight text-[var(--jc-text)]">
                    {c.cta2_title}
                  </div>
                  <div className="mt-0.5 text-[15px] leading-[1.45] text-[var(--jc-text)] font-medium">
                    {c.cta2_body}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3" data-testid="signup-callout-remembers-mobile">
                <span className="mt-0.5 flex-shrink-0 w-9 h-9 rounded-full bg-[var(--jc-blue)]/10 text-[var(--jc-blue)] grid place-items-center">
                  <Brain size={17} strokeWidth={2.6} />
                </span>
                <div className="min-w-0">
                  <div className="font-display font-extrabold text-[16.5px] leading-tight text-[var(--jc-text)]">
                    {c.cta3_title}
                  </div>
                  <div className="mt-0.5 text-[15px] leading-[1.45] text-[var(--jc-text)] font-medium">
                    {c.cta3_body}
                  </div>
                </div>
              </div>
            </div>

            {/* 7 · Hands-free message */}
            <div
              data-testid="signup-hands-free-pill-mobile"
              className="flex items-start gap-3 rounded-xl bg-[var(--jc-blue)]/[0.06] ring-1 ring-[var(--jc-blue)]/20 px-4 py-3"
            >
              <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-[var(--jc-blue)] text-white grid place-items-center">
                <Sparkles size={16} strokeWidth={2.6} />
              </span>
              <div className="text-[14.5px] leading-[1.5] text-[var(--jc-text)] font-bold">
                {c.pill_a}<br />
                <span className="font-medium text-[var(--jc-text)]">{c.pill_b}</span>
              </div>
            </div>
          </div>

          <div className="mb-4 text-center" data-testid="signup-overline">
            <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-[var(--jc-blue)]">
              {c.overline}
            </span>
          </div>

          <h1
            data-testid="signup-panel-heading"
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05] text-[var(--jc-text)] text-center"
          >
            {c.panel_heading_a}<br />
            {c.panel_heading_b}
          </h1>

          <p className="mt-5 text-[var(--jc-text-soft)] leading-relaxed text-center">
            {c.panel_sub}
          </p>

          <p className="mt-4 text-sm text-[var(--jc-text-soft)] text-center">
            {c.already_prefix}{" "}
            <Link
              to="/login"
              data-testid="signup-signin-link"
              className="text-[var(--jc-blue)] font-semibold hover:underline"
            >
              {c.already_link}
            </Link>
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="signup-form">
            <div className="relative">
              <User size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--jc-text-soft)]" />
              <input
                required
                data-testid="signup-name-input"
                value={form.full_name}
                onChange={update("full_name")}
                className={inputBase}
                placeholder={c.ph_full_name}
                autoComplete="name"
              />
            </div>
            <div className="relative">
              <Building2 size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--jc-text-soft)]" />
              <input
                required
                data-testid="signup-company-input"
                value={form.company_name}
                onChange={update("company_name")}
                className={inputBase}
                placeholder={c.ph_company}
                autoComplete="organization"
              />
            </div>
            <div className="relative">
              <Mail size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--jc-text-soft)]" />
              <input
                required
                type="email"
                data-testid="signup-email-input"
                value={form.email}
                onChange={update("email")}
                className={inputBase}
                placeholder={c.ph_email}
                autoComplete="email"
              />
            </div>
            <div className="relative">
              <Lock size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--jc-text-soft)]" />
              <input
                required
                type={showPassword ? "text" : "password"}
                minLength={8}
                data-testid="signup-password-input"
                value={form.password}
                onChange={update("password")}
                className={`${inputBase} pr-11`}
                placeholder={c.ph_password}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                data-testid="signup-password-toggle"
                aria-label={showPassword ? c.hide_pw : c.show_pw}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--jc-text-soft)] hover:text-[var(--jc-text)] transition"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="signup-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-lg font-semibold text-white bg-[var(--jc-blue)] hover:bg-[var(--jc-blue-700)] transition disabled:opacity-60 shadow-sm"
            >
              {loading ? c.submit_loading : (<>{c.submit_idle} <ArrowRight size={16} /></>)}
            </button>
          </form>

          {/* Approved trial + pricing panel (7-day free trial · $129/month
              includes 2 full users · additional users $20/month · standard
              onboarding).  This panel presents factual pricing only —
              there is no purchase mechanism, no Stripe Checkout entry
              point, and no external-purchase steering language — so it
              is safe to render inside the native (iOS/Android) shell
              under Strategy A.  Web behaviour unchanged. */}
          <div
            data-testid="signup-pricing-panel"
            className="mt-6 flex items-start gap-3 rounded-lg border border-[var(--jc-blue)]/25 bg-[var(--jc-blue)]/[0.04] px-4 py-4"
          >
            <ShieldCheck size={22} className="text-[var(--jc-blue)] shrink-0 mt-0.5" aria-hidden="true" />
            <div className="text-sm leading-relaxed text-[var(--jc-text-soft)]">
              <div className="font-semibold text-[var(--jc-text)]">
                {c.price_trial}
              </div>
              <div className="font-semibold text-[var(--jc-text)]">
                {c.price_main}
              </div>
              <div className="mt-1">{c.price_line1}</div>
              <div>{c.price_line2}</div>
              <div className="mt-2 font-semibold text-[var(--jc-text)]">
                {c.price_onboard}
              </div>
            </div>
          </div>

          {/* Security row + Privacy Policy link */}
          <div className="mt-5 pt-4 border-t border-[var(--jc-border)] flex items-center justify-between gap-3 text-xs text-[var(--jc-text-soft)]">
            <div className="flex items-center gap-2" data-testid="signup-security-copy">
              <Lock size={14} className="shrink-0" aria-hidden="true" />
              <span>{c.security}</span>
            </div>
            <Link
              to="/privacy"
              data-testid="signup-privacy-link"
              className="text-[var(--jc-blue)] font-semibold hover:underline whitespace-nowrap"
            >
              {c.privacy}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
